import { isStatusField, isSubStatusField } from './formStudioFormUtils';
import { getSubstatusOptionsForParentOption } from './statusOptionSubstatusUtils';
import { normalizeFieldOptions } from './fieldOptionsUtils';

function normalizeKey(value) {
  return String(value || '').trim();
}

export function getStatusRowId(status) {
  return status?.status_id ?? status?.id ?? status?.statusId ?? '';
}

export function mergeStatusRowPatch(existing, patch) {
  if (!existing) return patch;
  return {
    ...existing,
    ...patch,
    triggers: patch.triggers ?? existing.triggers ?? [],
    substatuses: patch.substatuses ?? existing.substatuses ?? [],
  };
}

export function getStatusFields(formFields = []) {
  return (formFields || []).filter(isStatusField);
}

export function getSubStatusFieldsForParent(formFields = [], parentKey = '') {
  const key = normalizeKey(parentKey);
  if (!key) return [];
  return (formFields || []).filter(
    (field) =>
      isSubStatusField(field) && normalizeKey(field.status_parent_key) === key
  );
}

export function filterStatusesForParent(statuses = [], parentKey = '') {
  const key = normalizeKey(parentKey);
  return (statuses || []).filter(
    (status) => normalizeKey(status.type_of_status) === key
  );
}

export function getSubstatusOptionsForStatusRow(subStatusField, statusOptionName) {
  return getSubstatusOptionsForParentOption(subStatusField, statusOptionName);
}

export function getSubstatusFromRow(statusRow, subName, subFieldKey) {
  const nameKey = normalizeKey(subName);
  const fieldKey = normalizeKey(subFieldKey);
  return (statusRow?.substatuses || []).find(
    (sub) =>
      normalizeKey(sub.name) === nameKey
      && (!fieldKey || normalizeKey(sub.type_of_status) === fieldKey)
  );
}

export function patchStatusTriggers(statusRow, triggers) {
  return mergeStatusRowPatch(statusRow, {
    triggers: Array.isArray(triggers) ? triggers : [],
  });
}

export function patchSubstatusTriggers(statusRow, subName, subFieldKey, triggers) {
  const nameKey = normalizeKey(subName);
  const fieldKey = normalizeKey(subFieldKey);
  const existingSubs = Array.isArray(statusRow?.substatuses) ? [...statusRow.substatuses] : [];
  const index = existingSubs.findIndex(
    (sub) =>
      normalizeKey(sub.name) === nameKey
      && normalizeKey(sub.type_of_status) === fieldKey
  );

  const nextSub = {
    ...(index >= 0 ? existingSubs[index] : { name: subName, type_of_status: subFieldKey }),
    name: subName,
    type_of_status: subFieldKey,
    triggers: Array.isArray(triggers) ? triggers : [],
  };

  if (index >= 0) {
    existingSubs[index] = nextSub;
  } else {
    existingSubs.push(nextSub);
  }

  return mergeStatusRowPatch(statusRow, { substatuses: existingSubs });
}

export function statusRowHasTriggers(statusRow) {
  const statusTriggers = Array.isArray(statusRow?.triggers) ? statusRow.triggers.length : 0;
  const subTriggers = (statusRow?.substatuses || []).reduce(
    (sum, sub) => sum + (Array.isArray(sub.triggers) ? sub.triggers.length : 0),
    0
  );
  return statusTriggers + subTriggers > 0;
}

export function countConfiguredTriggers(statuses = []) {
  return (statuses || []).reduce((count, row) => {
    const statusCount = Array.isArray(row.triggers) ? row.triggers.length : 0;
    const subCount = (row.substatuses || []).reduce(
      (sum, sub) => sum + (Array.isArray(sub.triggers) ? sub.triggers.length : 0),
      0
    );
    return count + statusCount + subCount;
  }, 0);
}

export function parentStatusFieldHasOptions(parentField) {
  return normalizeFieldOptions(parentField?.key_options).length > 0;
}
